import { useState } from "react";
import { patchOpsDeal } from "@/lib/opsPaperworkClient";
import { readyToDeliverBlockedReason } from "@/lib/ops/readyToDeliver";
import type { DealLinkedUnit } from "./opsPaperworkTypes";

export function OpsDealReadyToDeliverCard(props: {
  dealId: string;
  deal: Record<string, unknown>;
  linkedUnit: DealLinkedUnit | null;
  onChanged: () => Promise<void>;
}) {
  const ready = props.deal.ready_to_deliver === true;
  const notes = typeof props.deal.admin_hold_notes === "string" ? props.deal.admin_hold_notes : "";
  const [hold, setHold] = useState(notes);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const canCheck = props.linkedUnit?.can_mark_ready_to_deliver !== false;
  const blocked =
    !canCheck && props.linkedUnit
      ? readyToDeliverBlockedReason({
          unit: {
            ops_deal_id: props.dealId,
            sale_path: props.linkedUnit.sale_path,
            staged_at: props.linkedUnit.staged_at,
            build_completed_at: props.linkedUnit.build_completed_at,
            current_phase_id: props.linkedUnit.current_phase_id,
          },
          phase: { is_build_complete: props.linkedUnit.is_build_complete },
        })
      : null;

  async function saveReady(nextReady: boolean) {
    setBusy(true);
    setErr("");
    const r = await patchOpsDeal({
      id: props.dealId,
      ready_to_deliver: nextReady,
    });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    await props.onChanged();
  }

  async function saveHold(nextHold: string) {
    setBusy(true);
    setErr("");
    const r = await patchOpsDeal({
      id: props.dealId,
      admin_hold_notes: nextHold,
    });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      return;
    }
    await props.onChanged();
  }

  return (
    <section className="h-full rounded-lg border border-gray-200 p-3 space-y-2 dark:border-zinc-700">
      <div className="flex items-center justify-between gap-2">
        <h3
          className="text-sm font-semibold text-gray-800 dark:text-zinc-100"
          title="Does not send this deal to the plant. Check only after the building is finished and the customer is ready for logistics."
        >
          Ready to deliver
        </h3>
        <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-zinc-100">
          <input
            type="checkbox"
            checked={ready}
            disabled={busy || (!canCheck && !ready)}
            onChange={(e) => void saveReady(e.target.checked)}
          />
          Ready
        </label>
      </div>
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Admin hold notes (visible to other admins)
        <textarea
          className="mt-1 w-full text-xs border rounded px-2 py-1 dark:bg-zinc-900 dark:border-zinc-700"
          rows={2}
          value={hold}
          onChange={(e) => setHold(e.target.value)}
          onBlur={() => {
            if (hold !== notes) void saveHold(hold);
          }}
          placeholder="Why we are waiting (customer hold, site not ready…)"
        />
      </label>
      {blocked ? (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900">
          {blocked}
        </p>
      ) : null}
      {(() => {
        const meta = (props.deal.metadata ?? {}) as Record<string, unknown>;
        const alert = meta.configuration_alert as { summary?: string } | undefined;
        if (!alert || typeof alert !== "object") return null;
        return (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 dark:bg-amber-950/40 dark:text-amber-200 dark:border-amber-900">
            Recipe / COGS hole: {alert.summary ?? "unconfigured item"}
          </p>
        );
      })()}
      {err ? <p className="text-xs text-red-700">{err}</p> : null}
    </section>
  );
}
